import { isNeuropsychEvaluationProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import type {
  ProviderPairConversationObject,
  ProviderPairProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/conversation/CoordinatedCareConversation'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import type {
  CareHistoryEpisode,
  CareHistoryParticipantRole,
  CareHistoryProvider,
  CareHistoryProviderStatus,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'

import type { ProviderPairProviderProfile } from './providerPairUtils'

type ParsedCareHistoryProvider = CareHistoryProvider & {
  // False when current-episode evidence was missing or discarded during parsing.
  hasCompleteCurrentEpisodes?: false
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && Boolean(value.trim())

const isProviderStatus = (value: unknown): value is CareHistoryProviderStatus =>
  value === 'active' || value === 'unavailable' || value === 'deactivated'

const isParticipantRole = (value: unknown): value is CareHistoryParticipantRole =>
  value === 'primary_provider' || value === 'secondary_provider' || value === 'care_team_provider'

const stringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null)

const parseEpisodes = (value: unknown): CareHistoryEpisode[] =>
  Array.isArray(value)
    ? value.flatMap((episode) =>
        isRecord(episode) && isNonEmptyString(episode.episode_id)
          ? [
              {
                episode_id: episode.episode_id,
                program_name: stringOrNull(episode.program_name),
                participant_role: isParticipantRole(episode.participant_role) ? episode.participant_role : null,
                start_date: stringOrNull(episode.start_date),
                end_date: stringOrNull(episode.end_date),
                ...(isNonEmptyString(episode.partner) || episode.partner === null ? { partner: episode.partner } : {}),
              },
            ]
          : [],
      )
    : []

// Preserve identifiable providers when enrichment is malformed; existing-thread access comes from conversation capabilities.
export const parseProviderPairCareHistoryResponse = (value: unknown): ParsedCareHistoryProvider[] => {
  if (!Array.isArray(value)) {
    throw new Error('Malformed provider-pair care history response')
  }

  return value.flatMap((provider) => {
    if (!isRecord(provider) || !isNonEmptyString(provider.participant_lyra_id)) {
      console.warn('Discarding malformed provider-pair care history row')
      return []
    }
    const currentEpisodes = parseEpisodes(provider.current_episodes)
    const hasCompleteCurrentEpisodes =
      Array.isArray(provider.current_episodes) && currentEpisodes.length === provider.current_episodes.length
    return [
      {
        participant_lyra_id: provider.participant_lyra_id,
        display_name: stringOrNull(provider.display_name),
        lyra_type: stringOrNull(provider.lyra_type),
        profile_image_url: stringOrNull(provider.profile_image_url),
        provider_status: isProviderStatus(provider.provider_status) ? provider.provider_status : null,
        // Missing discovery eligibility fails closed without losing display metadata.
        can_message: provider.can_message === true,
        current_episodes: currentEpisodes,
        ...(hasCompleteCurrentEpisodes ? {} : { hasCompleteCurrentEpisodes: false as const }),
        historical_episodes: parseEpisodes(provider.historical_episodes),
      },
    ]
  })
}

// Partner varies over time (a provider can move from DirectAccess to BlendedCare), so only
// current episodes describe how the counterpart serves this client now.
const getCurrentPrograms = (provider: CareHistoryProvider): ProviderPairProgram[] => {
  const programs = new Map<string, ProviderPairProgram>()
  provider.current_episodes.forEach(({ program_name: name, partner }) => {
    if (isNonEmptyString(name)) {
      programs.set(JSON.stringify([name, partner ?? null]), { name, partner: partner ?? null })
    }
  })
  return [...programs.values()]
}

export const fallbackProviderPairProfile = (
  providerLyraId: string,
  displayName?: string,
): ProviderPairProviderProfile => ({
  providerLyraId,
  displayName: displayName?.trim() || 'Provider',
  relationshipStatus: undefined,
  status: undefined,
  programs: [],
})

// A readable conversation must remain visible even when profile lookup fails or the
// counterpart is absent from the care-history response. Callers can pass another known
// name (e.g. from the episode roster) so tabs stay distinguishable before the generic label.
export const getProviderPairCareHistoryProfile = (
  providers: ParsedCareHistoryProvider[],
  providerLyraId: string,
  fallbackDisplayName?: string,
): ProviderPairProviderProfile => {
  const provider = providers.find(({ participant_lyra_id: id }) => id === providerLyraId)
  if (!provider) {
    return fallbackProviderPairProfile(providerLyraId, fallbackDisplayName)
  }
  return {
    providerLyraId,
    displayName: provider.display_name?.trim() || fallbackDisplayName?.trim() || 'Provider',
    relationshipStatus: provider.current_episodes.length
      ? 'current'
      : provider.hasCompleteCurrentEpisodes !== false && provider.historical_episodes.length
        ? 'historical'
        : undefined,
    status: provider.provider_status ?? undefined,
    programs: getCurrentPrograms(provider),
  }
}

// Use only episodes associated with this conversation, including retained historical care.
// Multiple associated programs are displayed together until a primary-program rule is defined.
export const getProviderPairProgramNames = (
  providers: CareHistoryProvider[],
  providerLyraId: string,
  episodeIds: ProviderPairConversationObject['episode_ids'],
): string[] => {
  const provider = providers.find(({ participant_lyra_id }) => participant_lyra_id === providerLyraId)
  const episodes = [...(provider?.current_episodes ?? []), ...(provider?.historical_episodes ?? [])]
  return [
    ...new Set(
      episodes.flatMap(({ episode_id, program_name }) =>
        episodeIds.includes(episode_id) && isNonEmptyString(program_name)
          ? // Care history uses the LyraTherapy neuropsych name, which ProgramConfigs is not keyed by.
            [isNeuropsychEvaluationProgram(program_name) ? ProgramNames.NeuroPsychologicalEvaluations : program_name]
          : [],
      ),
    ),
  ]
}
